import React from 'react';
import { HardDrive, Loader2, X } from 'lucide-react';
import ConfirmModal from './ConfirmModal';
import localCacheService from '../services/localCacheService';

/**
 * CacheBanner — 本地缓存横幅 + 确认弹窗
 * 从 EditorContent 提取的独立组件，拥有自己的 UI 和事件处理，
 * 只在 cacheInvalid 变化时重渲染，不影响主画布。
 */
const CacheBanner = React.memo(function CacheBanner({
  cacheInvalid, setCacheInvalid,
  cacheReconfiguring, setCacheReconfiguring,
  showCacheConfirm, setShowCacheConfirm,
  setAlertMsg, nodesRef, globalAssetsRef, crewIdRef, crewNameRef,
}) {
  if (!cacheInvalid && !showCacheConfirm) return null;

  return (
    <>
      {cacheInvalid && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10001,
          background: cacheInvalid === 'invalid'
            ? 'linear-gradient(135deg, rgba(127, 29, 29, 0.95), rgba(153, 27, 27, 0.95))'
            : cacheInvalid === 'need-reauth'
            ? 'linear-gradient(135deg, rgba(120, 53, 15, 0.95), rgba(146, 64, 14, 0.95))'
            : 'linear-gradient(135deg, rgba(30, 58, 138, 0.95), rgba(29, 78, 216, 0.95))',
          borderBottom: cacheInvalid === 'invalid' ? '1px solid rgba(239, 68, 68, 0.5)' : cacheInvalid === 'need-reauth' ? '1px solid rgba(234, 179, 8, 0.5)' : '1px solid rgba(96, 165, 250, 0.5)',
          padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
          backdropFilter: 'blur(8px)', animation: 'slideDown 0.3s ease'
        }}>
          <HardDrive size={16} color={cacheInvalid === 'invalid' ? '#fca5a5' : cacheInvalid === 'need-reauth' ? '#fde68a' : '#93c5fd'} />
          <span style={{ color: cacheInvalid === 'invalid' ? '#fecaca' : cacheInvalid === 'need-reauth' ? '#fef9c3' : '#dbeafe', fontSize: 13 }}>
            {cacheInvalid === 'invalid'
              ? '本地缓存文件夹不可用（文件夹已删除），媒体文件将从服务器加载'
              : cacheInvalid === 'need-reauth'
              ? '本地缓存权限已过期，点击恢复即可继续使用加速缓存'
              : '尚未配置本地缓存文件夹，配置后可加速媒体加载并支持离线访问'}
          </span>
          <button
            onClick={cacheInvalid === 'need-reauth'
              ? async () => {
                  setCacheReconfiguring(true);
                  const result = await localCacheService.reauthorizeCacheFolder();
                  setCacheReconfiguring(false);
                  if (result.success) { setCacheInvalid(false); }
                  else { setShowCacheConfirm(true); }
                }
              : () => setShowCacheConfirm(true)
            }
            disabled={cacheReconfiguring}
            style={{
              padding: '5px 14px', borderRadius: 6,
              border: cacheInvalid === 'invalid' ? '1px solid rgba(239, 68, 68, 0.6)' : cacheInvalid === 'need-reauth' ? '1px solid rgba(234, 179, 8, 0.6)' : '1px solid rgba(96, 165, 250, 0.6)',
              background: cacheInvalid === 'invalid' ? 'rgba(239, 68, 68, 0.3)' : cacheInvalid === 'need-reauth' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(59, 130, 246, 0.3)',
              color: cacheInvalid === 'invalid' ? '#fecaca' : cacheInvalid === 'need-reauth' ? '#fef9c3' : '#dbeafe',
              fontSize: 12, fontWeight: 'bold', cursor: cacheReconfiguring ? 'wait' : 'pointer',
              display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap', transition: 'all 0.2s'
            }}
            onMouseEnter={e => { if (!cacheReconfiguring) e.currentTarget.style.background = cacheInvalid === 'invalid' ? 'rgba(239, 68, 68, 0.5)' : cacheInvalid === 'need-reauth' ? 'rgba(234, 179, 8, 0.5)' : 'rgba(59, 130, 246, 0.5)'; }}
            onMouseLeave={e => { if (!cacheReconfiguring) e.currentTarget.style.background = cacheInvalid === 'invalid' ? 'rgba(239, 68, 68, 0.3)' : cacheInvalid === 'need-reauth' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(59, 130, 246, 0.3)'; }}>
            {cacheReconfiguring ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : null}
            {cacheInvalid === 'invalid' ? '重新配置' : cacheInvalid === 'need-reauth' ? '恢复缓存' : '配置缓存'}
          </button>
          <button onClick={() => setCacheInvalid(false)} style={{ background: 'transparent', border: 'none', color: cacheInvalid === 'invalid' ? '#fca5a5' : cacheInvalid === 'need-reauth' ? '#fde68a' : '#93c5fd', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', opacity: 0.7 }}>
            <X size={14} />
          </button>
          <style>{`@keyframes slideDown { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
        </div>
      )}

      {showCacheConfirm && (
        <ConfirmModal
          title={cacheInvalid === 'invalid' ? "重新配置本地缓存" : "配置本地缓存"}
          message={cacheInvalid === 'invalid'
            ? "浏览器将请求文件夹读写权限（正常安全机制）。请选择一个本地文件夹用于缓存媒体文件。"
            : "选择一个本地文件夹用于缓存媒体文件，可大幅加速加载速度并支持离线查看。浏览器将请求文件夹读写权限（正常安全机制）。"}
          confirmText="选择文件夹" cancelText="取消"
          onConfirm={async () => {
            setShowCacheConfirm(false);
            setCacheReconfiguring(true);
            const result = await localCacheService.requestCacheFolder();
            setCacheReconfiguring(false);
            if (result.success) {
              setCacheInvalid(false);
              setAlertMsg({ text: '本地缓存已重新配置，正在同步文件...', type: 'success' });
              const cid = crewIdRef.current;
              const cname = crewNameRef.current;
              if (cid) {
                (async () => {
                  try {
                    const currentNodes = nodesRef.current || [];
                    for (const node of currentNodes) {
                      const output = node.data?.output;
                      if (!output || output.startsWith('blob:') || output.startsWith('data:')) continue;
                      const fileType = node.data.type === 'video' ? 'video' : node.data.type === 'audio' ? 'audio' : 'image';
                      await localCacheService.syncToLocalFolder(output, cid, cname, fileType);
                    }
                    const currentAssets = globalAssetsRef.current || [];
                    for (const asset of currentAssets) {
                      const url = asset.url;
                      if (!url || url.startsWith('blob:') || url.startsWith('data:')) continue;
                      const fileType = asset.category === 'videos' ? 'video' : asset.category === 'audios' ? 'audio' : 'image';
                      await localCacheService.syncToLocalFolder(url, cid, cname, fileType);
                    }
                  } catch (err) { console.warn('缓存同步出错（不影响使用）:', err); }
                })();
              }
            } else if (result.error && result.error !== '已取消选择') {
              setAlertMsg({ text: result.error, type: 'error' });
            }
          }}
          onCancel={() => setShowCacheConfirm(false)}
        />
      )}
    </>
  );
});

export default CacheBanner;
